import { dateFromPartial, isPartialDate, nowPartialDate, nowTime, sortPartialDate, sortTime } from 'neuro-utils';
import store from 'Store/index';
import { filterByDocumentType, filterDocumentsByType, sourceIsIntegration } from 'Utility/documentHandling';
import { path, uniq } from 'Utility/ramdaReplacement';
import {
  regimenTypesByMed,
  regimenTypesByPlatform,
  medicationSubstancesByCategory,
  regimenTypesBySubstanceAndDosageform,
  getAtcCodesByPlatform,
} from '../Document/config';
import { isParkinsonInfusionType } from '../Document/Form/Medication/RegimenDialog/utils/isParkinsonInfusionType';

/**
 * Map medications and their substances to categories
 * @param {IDocumentWithTypeV2<'medication'>[]} documents - Medication documents
 * @returns {ISubstanceCategories} Medication categories object with number of medications in each category
 */
export const medicationSubstanceCategories = (documents: IDocumentWithTypeV2<'medication'>[]): ISubstanceCategories => {
  const catNumbers = {} as { [key: string]: number };
  documents.forEach(d => {
    Object.keys(medicationSubstancesByCategory).forEach((c: string) => {
      if (medicationSubstancesByCategory[c]?.test(d.medicationSubstances ?? '')) {
        if (catNumbers[c]) {
          catNumbers[c]++;
        } else catNumbers[c] = 1;
      }
    });
  });
  return catNumbers;
};

interface ISubstanceCategories {
  levodopa?: number;
  maobInhibitors?: number;
  dopamineAgonists?: number;
}

/**
 * Get disease modifying medications by platform-specific ATC-codes
 * @param {IDocumentWithTypeV2<'medication'>[]} documents - All medication documents
 * @param {string} platform - Platform (eg. sma, parkinson, ...)
 * @returns {IDocumentWithTypeV2<'medication'>[]} Medication documents filtered by platform-specific ATC-codes to only include disease modifying medications
 */
export const getDiseaseModifyingMedications = (
  medicationSettings: IOrganizationNeuroSettings['medicationSettings'] | undefined,
  documents: IDocumentWithTypeV2<'medication'>[],
  platform?: Platform,
): IDocumentWithTypeV2<'medication'>[] => {
  const atcCodesByPlatform = getAtcCodesByPlatform(medicationSettings, platform);
  if (platform && Array.isArray(documents)) {
    return documents.filter(document =>
      atcCodesByPlatform.find((code: string) => code === (document.atc ?? '').substring(0, code.length)),
    );
  }
  return documents;
};

/**
 * Regimen strengths into a string
 * @param {IStrengths} s - Strengths
 */
export const formatStrenghtString = (s: IStrengths): string =>
  Object.values(s)
    .filter((s): s is string => !!s)
    .join('/');

/**
 * Sort given strengths
 * @param {IStrengths} s1 - First strengths
 * @param {IStrengths} s2 - Second strengths
 */
export const sortByStr = (s1?: IStrengths, s2?: IStrengths): number => {
  // By which key the strengths are sorted by (usually the first one)
  const sortedKey = s1 ? Object.keys(s1)[0] : s2 ? Object.keys(s2)[0] : '';

  if (!sortedKey) return 0;

  const first =
    s1 && !isNaN(parseInt(s1[sortedKey]?.split(' ')[0] ?? '', 10))
      ? parseInt(s1[sortedKey]?.split(' ')[0] ?? '', 10)
      : 0;
  const second =
    s2 && !isNaN(parseInt(s2[sortedKey]?.split(' ')[0] ?? '', 10))
      ? parseInt(s2[sortedKey]?.split(' ')[0] ?? '', 10)
      : 0;

  return first > second ? 1 : second > first ? -1 : 0;
};

/**
 * Match medication name to its regimen type
 * @param {string | null | undefined} medicationName - Medication name
 * @param {string | null | undefined} substances - Medication substances
 * @param {string | null | undefined} dosageForm - Medication dosage form
 * @returns {string} Regimen type
 */
export const getRegimenType = (
  medicationName: IDocumentWithTypeV2<'medication'>['medicationName'],
  substances?: IDocumentWithTypeV2<'medication'>['medicationSubstances'],
  dosageForm?: IDocumentWithTypeV2<'medication'>['dosageForm'],
): RegimenType | 'static' | undefined => {
  if (substances && dosageForm) {
    const matchRegimen =
      regimenTypesBySubstanceAndDosageform.find(s => s.substance.test(substances) && s.dosageForm.test(dosageForm))
        ?.regimen ?? null;
    if (matchRegimen) return matchRegimen;
  }
  const type = (Object.keys(regimenTypesByMed) as Array<keyof typeof regimenTypesByMed>).map(reg => {
    if (medicationName && regimenTypesByMed[reg]?.includes(medicationName.toUpperCase())) {
      return reg;
    } else return undefined;
  });
  return type.find(t => !!t) ?? 'default';
};

/**
 * Get possible medication regimen types by medication, platform and dosage form
 * @param {string} medication - Medication name
 * @param {string} platform - Platform (eg. sma, parkinson, ...)
 * @param {string} dosageForm - Dosage form, i.e. a string possibly containing word "liuos"
 * @return {string[]} List of regimen types
 */
export const getRegimenOptions = (
  medicationName: IDocumentWithTypeV2<'medication'>['medicationName'],
  platform: Platform | undefined,
  isClinicalStudy: IDocumentWithTypeV2<'medication'>['isClinicalStudy'],
): string[] => {
  if (path([0], isClinicalStudy) === true) {
    return regimenTypesByPlatform['other'] ?? [];
  }

  const regimenType = getRegimenType(medicationName);
  let regimens = regimenType ? [regimenType] : [];

  // regimen types by platform
  if (platform && regimenTypesByPlatform[platform]) {
    regimens = [...regimens, ...regimenTypesByPlatform[platform]];
  } else {
    regimens = [...regimens, ...(regimenTypesByPlatform['other'] ?? [])];
  }
  const regimTypes = uniq(regimens);

  return regimTypes;
};

/**
 * Check if medication has ended
 * @param {TFormDocument<IDocumentWithTypeV2<'medication'>>} document - Medication document to be checked
 * @return {boolean} True if hasEnded is true and endDate has passed or is empty, false otherwise
 */
export const medicationEnded = (document: TFormDocument<IDocumentWithTypeV2<'medication'>>): boolean => {
  if (document.hasEnded?.[0] === true) {
    if (document.endDate && sortPartialDate(document.endDate, nowPartialDate()) === 1) {
      return false;
    }
    return true;
  }
  return false;
};

export const numberOfMedicines = (documents: IDocumentWithTypeV2<'medication'>[]): number | undefined => {
  // Filter out medications that have ended
  const meds =
    documents.length > 0
      ? documents.filter(item => medicationEnded(item) !== true).map(item => item.medicationName)
      : [];

  if (meds.length > 0) {
    // Filter out duplicates here
    return meds.filter((item, index) => meds.indexOf(item) === index).length;
  } else {
    return undefined;
  }
};

/**
 * Check whether document has an active pause.
 * @param doc document whose pauses to check
 * @returns true if document has an active pause, false if not
 */
export const pauseIsActive = (doc: TFormDocument<IDocumentWithTypeV2<'medication'>>): boolean => {
  // pause is not active if there are no pauses or if medication is ended
  if (!doc.pauses || doc.pauses.length < 1 || medicationEnded(doc)) return false;
  // pause has been started if start time is now or in the past, not in the future
  const startedPauses = doc.pauses.filter(p => {
    const startInPast: boolean = sortPartialDate(p.startDate, nowPartialDate()) === -1;
    const startNowOrEarlierToday: boolean =
      sortPartialDate(p.startDate, nowPartialDate()) === 0 && sortTime(p.startTime, nowTime()) <= 0;
    return startInPast || startNowOrEarlierToday;
  });
  // pause is still active if it has no end, it ends in a later day or later today
  const stillActivePauses = startedPauses.filter(p => {
    const noEnd = !isPartialDate(p.endDate);
    const endInFuture = sortPartialDate(nowPartialDate(), p.endDate) === -1;
    const endLaterToday = sortPartialDate(nowPartialDate(), p.endDate) === 0 && sortTime(p.endTime, nowTime()) > 0;
    return noEnd || endInFuture || endLaterToday;
  });
  return stillActivePauses.length >= 1;
};

/**
 * Find active medication documents
 * @param {Array<IDocumentV2>} documents Array of documents
 * @returns {Array<IDocumentWithTypeV2<'medication'>>} Array of active medication documents
 */
export const findActiveMedications = (documents: Array<IDocumentV2>): Array<IDocumentWithTypeV2<'medication'>> => {
  return filterByDocumentType(documents, 'medication').filter(d => !medicationEnded(d) && !pauseIsActive(d));
};

/**
 * Find paused medication documents
 * @param {Array<IDocumentV2>} documents Array of documents
 * @returns {Array<IDocumentWithTypeV2<'medication'>>} Array of paused medication documents
 */
export const findPausedMedications = (documents: Array<IDocumentV2>): Array<IDocumentWithTypeV2<'medication'>> => {
  return filterByDocumentType(documents, 'medication').filter(d => !medicationEnded(d) && pauseIsActive(d));
};

/**
 * Find ended medication documents
 * @param {Array<IDocumentV2>} documents Array of documents
 * @returns {Array<IDocumentWithTypeV2<'medication'>>} Array of ended medication documents
 */
export const findEndedMedications = (documents: Array<IDocumentV2>): Array<IDocumentWithTypeV2<'medication'>> => {
  return filterByDocumentType(documents, 'medication').filter(d => medicationEnded(d));
};

/**
 * Find other treatments
 * @param {Array<any>} documents Array of documents
 * @returns {Array<IDocumentWithTypeV2<'supplementalTreatment' | 'procedure'>>} Array of other treatments
 */
export const findOtherTreatments = (
  documents: Array<IDocumentV2>,
): Array<IDocumentWithTypeV2<'supplementalTreatment' | 'procedure'>> => {
  return filterDocumentsByType(documents, ['supplementalTreatment', 'procedure']);
};

/**
 * Find periods without medication
 * @param {Array<IDocumentV2>} documents Array of documents
 * @returns {Array<IDocumentWithTypeV2<'periodWithoutMedication'>>} Array of periods
 */
export const findPeriodsWithoutMedication = (
  documents: Array<IDocumentV2>,
): Array<IDocumentWithTypeV2<'periodWithoutMedication'>> => {
  return filterByDocumentType(documents, 'periodWithoutMedication');
};

/**
 * See if locked regimen can be edited based on regimen type and document status
 * @param {IDocumentWithTypeV2<'medication'>} doc - Medication document
 * @returns {boolean} Can or cannot edit
 */
export const canEditLockedRegimen = (doc: IDocumentWithTypeV2<'medication'>, regimen: Regimen): boolean => {
  if (!doc._lockedFor) return false;
  if (isParkinsonInfusionType(doc) && regimen.regimenType === 'custom') return true;
  return false;
};

export interface IFilterSettings {
  medicationName?: string;
  date?: { startDate?: PartialDate; endDate?: PartialDate };
  status?: Array<'inUse' | 'toStart' | 'paused' | 'ended'>;
  platformSpecificDrugsOnly?: boolean;
  regimenType?: Array<'default' | 'single-dose' | 'onDemand' | 'custom' | 'static' | 'other'>;
  source?: Array<'stellarq' | 'integration' | string>; // Strings are organization ids
}

/**
 * Filter medications based on an object (of arrays) of different rules
 * @param {Object} settings - Filter by these rules
 * @returns
 */
export const filterMedications =
  (medicationSettings: IOrganizationNeuroSettings['medicationSettings'] | undefined, settings: IFilterSettings) =>
  (doc: IDocumentWithTypeV2<'medication'>): boolean => {
    let filterMatch = true;

    const reduxStore: IState = store.getState();

    const ruleNames = Object.keys(settings);
    ruleNames.some(rule => {
      /**
       * Go though the rules until any of them returns true, and changes filterMatch to false meaning that the medication should be filtered.
       * Every rule should return true only if the rule is set and the medication does not match it.
       */
      switch (rule) {
        case 'medicationName': {
          const filterName = settings['medicationName'];
          const regexp = new RegExp(`${filterName}`, 'i'); // case insensitive
          if (
            !filterName ||
            filterName.length === 0 ||
            doc.medicationName?.match(regexp) ||
            doc.medicationSubstances?.match(regexp)
          ) {
            break;
          } else {
            // No match for the medication
            filterMatch = false;
            // Break some-loop
            return true;
          }
        }

        case 'date': {
          const medicationStartDate = doc.usageStartDate
            ? dateFromPartial(doc.usageStartDate)
            : doc.startDate && dateFromPartial(doc.startDate);
          const medicationEndDate =
            Array.isArray(doc.hasEnded) && doc.hasEnded[0] === true && doc.endDate && dateFromPartial(doc.endDate);

          const filterStartDate = settings['date']?.startDate && dateFromPartial(settings['date'].startDate);
          const filterEndDate = settings['date']?.endDate && dateFromPartial(settings['date'].endDate);
          if (
            (medicationStartDate ?? 0) < (filterStartDate ?? 0) ||
            (medicationEndDate ?? 10000000000000) > (filterEndDate ?? 10000000000000) // If end date doesn't exists, then use year ~2286
          ) {
            filterMatch = false;
            return true;
          }
          break;
        }

        case 'status': {
          const statuses = settings['status'] ?? [];
          const medicationStartDate = doc.usageStartDate
            ? dateFromPartial(doc.usageStartDate)
            : doc.startDate && dateFromPartial(doc.startDate);

          if (statuses.length === 0) break;
          if (
            statuses.includes('inUse') &&
            (medicationStartDate?.valueOf() ?? 0) <= Date.now() && // This also shows medications without date
            !medicationEnded(doc) &&
            !pauseIsActive(doc)
          ) {
            break;
          }
          if (statuses.includes('toStart') && (medicationStartDate?.valueOf() ?? 0) > Date.now()) {
            break;
          }
          if (statuses.includes('paused') && !medicationEnded(doc) && pauseIsActive(doc)) {
            break;
          }
          if (statuses.includes('ended') && medicationEnded(doc)) {
            break;
          }
          filterMatch = false;
          return true;
        }

        case 'platformSpecificDrugsOnly': {
          const platformSpecificDrugsOnly = settings['platformSpecificDrugsOnly'];
          const platform = reduxStore.session.platforms?.selected;

          let isSpecific: boolean = false;
          if (platform) {
            if (
              getAtcCodesByPlatform(medicationSettings, platform).some(
                datc => datc === doc.atc?.substring(0, datc.length),
              )
            ) {
              isSpecific = true;
            }
          }
          if (!platformSpecificDrugsOnly || isSpecific) {
            break;
          }
          filterMatch = false;
          return true;
        }

        case 'regimenType': {
          if (!(doc.regimen ?? []).find(d => d.regimenType)) break;

          if (settings['regimenType'] && settings['regimenType'].length === 0) break;
          if (doc.regimen?.some(r => r.regimenType && settings['regimenType']?.includes(r.regimenType))) {
            break;
          }
          filterMatch = false;
          return true;
        }

        case 'source': {
          const sources = settings['source'] ?? [];
          const userOrg = reduxStore.session.data?.orgid;

          if (sources.length === 0) break;
          if (
            sources.includes('stellarq') &&
            doc._ownerOrg === userOrg &&
            !(doc._lockedFor && sourceIsIntegration(doc._source))
          ) {
            break;
          }
          if (
            sources.includes('integration') &&
            doc._ownerOrg === userOrg &&
            doc._lockedFor &&
            sourceIsIntegration(doc._source)
          ) {
            break;
          }
          if (sources.length > 0 && sources.includes(doc._ownerOrg || '')) {
            break;
          }
          filterMatch = false;
          return true;
        }

        default:
          break;
      }
      return false;
    });

    return filterMatch;
  };
